// src/lib/snapProject.js
//
// Project a lat/lng onto the nearest point of a GeoJSON LineString. Used by
// Live Map's snap-to-road to keep the animated marker glued to the road
// polyline returned by OSRM, instead of either freezing it between batches
// (the v1 bug) or letting it drift off-road (the current behavior).
//
// Pure functions, no React. Lives outside hooks so the projection can be
// tested standalone and reused later by the mobile Fleet animator.

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance between two lat/lng points (meters). */
export function distanceMeters(lat1, lng1, lat2, lng2) {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/**
 * Project point P onto segment AB in 2D (treating lat/lng as planar). For the
 * trail-fragment distances we deal with (<1 km between OSRM polyline vertices)
 * the flat-earth approximation is well under a meter of error — fine for a
 * marker-snap UX. Returns the projected point's [lng, lat].
 */
function projectOnSegment(pLng, pLat, aLng, aLat, bLng, bLat) {
  const dx = bLng - aLng;
  const dy = bLat - aLat;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-18) return [aLng, aLat];
  let t = ((pLng - aLng) * dx + (pLat - aLat) * dy) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return [aLng + t * dx, aLat + t * dy];
}

/**
 * Find the closest point on a GeoJSON LineString to (lat, lng). Returns null
 * if the polyline has fewer than two vertices. Otherwise returns the projected
 * lat/lng + the distance to it in meters AND the segment index where the
 * projection landed (so callers can truncate the polyline at the projection).
 *
 * @param {number} lat
 * @param {number} lng
 * @param {{ type: 'LineString', coordinates: [number, number][] }} polyline
 * @returns {{ latitude: number, longitude: number, distanceMeters: number, segmentIndex: number } | null}
 */
export function projectOnPolyline(lat, lng, polyline) {
  const coords = polyline?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;

  let bestLat = 0;
  let bestLng = 0;
  let bestDist = Infinity;
  let bestIdx = 0;

  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    if (!a || !b || a.length < 2 || b.length < 2) continue;
    const [projLng, projLat] = projectOnSegment(lng, lat, a[0], a[1], b[0], b[1]);
    const d = distanceMeters(lat, lng, projLat, projLng);
    if (d < bestDist) {
      bestDist = d;
      bestLat = projLat;
      bestLng = projLng;
      bestIdx = i;
    }
  }

  if (!Number.isFinite(bestDist)) return null;
  return { latitude: bestLat, longitude: bestLng, distanceMeters: bestDist, segmentIndex: bestIdx };
}

/**
 * Truncate a GeoJSON LineString at a projection point so the resulting
 * polyline ends EXACTLY at the projection. Used for the snapped trail: we
 * want the trail to terminate at the marker, not at OSRM's last matched
 * vertex (which sits ahead of the marker when the animator is rendering the
 * marker "1 fix behind" real time).
 *
 * Keeps coordinates [0..segmentIndex] intact, then appends the projection
 * point as the new last vertex. If the projection coincides exactly with
 * coordinates[segmentIndex] (segmentT=0), drops the duplicate to avoid a
 * zero-length terminal segment.
 *
 * @returns {{ type: 'LineString', coordinates: [number, number][] }}
 */
export function truncatePolylineAtProjection(polyline, projection) {
  const coords = polyline?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2 || !projection) return polyline;
  const idx = Number.isInteger(projection.segmentIndex) ? projection.segmentIndex : 0;
  if (idx < 0 || idx >= coords.length - 1) return polyline;
  const kept = coords.slice(0, idx + 1);
  const last = kept[kept.length - 1];
  const projLng = projection.longitude;
  const projLat = projection.latitude;
  // Avoid a degenerate trailing zero-length segment.
  if (last && Math.abs(last[0] - projLng) < 1e-9 && Math.abs(last[1] - projLat) < 1e-9) {
    return { type: 'LineString', coordinates: kept };
  }
  kept.push([projLng, projLat]);
  return { type: 'LineString', coordinates: kept };
}

/**
 * Interpolate a marker ALONG a snapped road polyline between two GPS fixes.
 *
 * Instead of a straight-line lerp between `prev` and `target` (which cuts
 * across curves), this projects both fixes onto the polyline, then returns the
 * point at arc-length `arcPrev + t·(arcTarget − arcPrev)` walked along the road
 * — so a marker mid-tween follows the road's bends rather than the chord. Also
 * returns the `segmentIndex` the along-point falls in, so the trail can be
 * truncated at exactly the same point (marker + trail stay glued).
 *
 * Returns null (caller falls back to straight-projection) when the polyline is
 * degenerate or EITHER fix projects farther than `maxSnapMeters` from the road
 * (e.g. OSRM hasn't matched that stretch yet, so we can't trust the walk).
 *
 * @param {{latitude:number, longitude:number}} prev    fix N-1
 * @param {{latitude:number, longitude:number}} target  fix N
 * @param {number} t  animation fraction in [0,1]
 * @param {{ type:'LineString', coordinates:[number, number][] }} polyline
 * @param {number} maxSnapMeters  per-fix projection cap
 * @returns {{ latitude:number, longitude:number, segmentIndex:number } | null}
 */
export function interpolateAlongPolyline(prev, target, t, polyline, maxSnapMeters) {
  const coords = polyline?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2 || !prev || !target) return null;

  const pPrev = projectOnPolyline(prev.latitude, prev.longitude, polyline);
  const pTarget = projectOnPolyline(target.latitude, target.longitude, polyline);
  if (!pPrev || !pTarget) return null;
  if (Number.isFinite(maxSnapMeters) &&
      (pPrev.distanceMeters > maxSnapMeters || pTarget.distanceMeters > maxSnapMeters)) {
    return null;
  }

  // Cumulative arc-length to each vertex.
  const cum = [0];
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    cum.push(cum[i] + distanceMeters(a[1], a[0], b[1], b[0]));
  }
  const total = cum[cum.length - 1];

  // Arc-length of a projection = arc to its segment's start + distance into it.
  const arcOf = (proj) => {
    const seg = coords[proj.segmentIndex];
    return cum[proj.segmentIndex] + distanceMeters(seg[1], seg[0], proj.latitude, proj.longitude);
  };
  const arcPrev = arcOf(pPrev);
  const arcTarget = arcOf(pTarget);

  const clampedT = t < 0 ? 0 : t > 1 ? 1 : t;
  let desired = arcPrev + (arcTarget - arcPrev) * clampedT;
  if (desired < 0) desired = 0;
  else if (desired > total) desired = total;

  // Walk to the segment containing `desired`.
  let i = 0;
  while (i < coords.length - 2 && cum[i + 1] < desired) i++;
  const segStart = cum[i];
  const segLen = cum[i + 1] - segStart;
  const within = segLen > 1e-9 ? (desired - segStart) / segLen : 0;
  const a = coords[i];
  const b = coords[i + 1];
  return {
    latitude: a[1] + (b[1] - a[1]) * within,
    longitude: a[0] + (b[0] - a[0]) * within,
    segmentIndex: i,
  };
}

/**
 * Count how many vehicles are currently being drawn ON the snapped road versus
 * how many have a road match at all — the metric behind the Live Map snap
 * popover's "X / Y vehicles snapped to road" readout.
 *
 * `matched` is every device that has BOTH a usable polyline (>= 2 vertices) AND
 * a finite raw position, so it's projectable. `snapped` is the subset whose raw
 * position projects onto its polyline within `thresholdM` meters — i.e. the
 * marker stays glued to the road rather than falling through to raw GPS.
 *
 * Pure: reads the same Redux `positions` object and module-scope `polylineById`
 * Map the snap hook already owns; no React, no side effects.
 *
 * @param {Record<string|number, { latitude: number, longitude: number }>} positions
 * @param {Map<number, { type: 'LineString', coordinates: [number, number][] }>} polylineById
 * @param {number} thresholdM
 * @returns {{ snapped: number, matched: number }}
 */
export function countSnapped(positions, polylineById, thresholdM) {
  let snapped = 0;
  let matched = 0;
  if (!polylineById || typeof polylineById.forEach !== 'function') {
    return { snapped, matched };
  }
  polylineById.forEach((polyline, deviceId) => {
    const pos = positions?.[deviceId];
    if (!pos || !Number.isFinite(pos.latitude) || !Number.isFinite(pos.longitude)) return;
    const projection = projectOnPolyline(pos.latitude, pos.longitude, polyline);
    if (!projection) return;
    matched += 1;
    if (projection.distanceMeters <= thresholdM) snapped += 1;
  });
  return { snapped, matched };
}
